/**
 * ASSIST-01 — what the client knows about Assist, and the rules it applies.
 *
 * Imports nothing, so `tsx` tests can load it directly (the src/lib/chatLead.ts
 * pattern: lib/api/client.ts reads `import.meta.env` at module scope and throws
 * under bare `tsx`).
 *
 * The server is the authority. `get_assist_public_config()` says which mode is
 * on and whether the caller is a tester; `assist_gate()` refuses every Assist
 * RPC that the mode does not allow (brief R2a). These helpers only decide what
 * to SHOW, and every one of them fails closed: anything unexpected is `off`,
 * not a tester, not eligible.
 */

export type AssistMode = 'off' | 'waitlist' | 'live';

export interface AssistPublicConfig {
  mode: AssistMode;
  isTester: boolean;
  /** Raw `listings.property_type` values in the pilot (D4: apartment). */
  pilotPropertyTypes: readonly string[];
  /** Exact `listings.city` spellings in the pilot (G0b A13: Nairobi). */
  pilotCities: readonly string[];
}

/** What every failure, every unknown shape and every loading state reads as. */
export const ASSIST_OFF_CONFIG: AssistPublicConfig = Object.freeze({
  mode: 'off',
  isTester: false,
  pilotPropertyTypes: Object.freeze([]) as readonly string[],
  pilotCities: Object.freeze([]) as readonly string[],
});

function stringList(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Read the RPC's JSON. Never throws. `null` (a failed call, or the demo
 * backend's answer to an RPC it does not know) is `off`.
 */
export function parseAssistPublicConfig(raw: unknown): AssistPublicConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return ASSIST_OFF_CONFIG;
  const obj = raw as Record<string, unknown>;
  const mode: AssistMode =
    obj.mode === 'waitlist' || obj.mode === 'live' ? obj.mode : 'off';
  return {
    mode,
    isTester: obj.is_tester === true,
    pilotPropertyTypes: stringList(obj.pilot_property_types),
    pilotCities: stringList(obj.pilot_cities),
  };
}

/**
 * Which listing card this viewer gets. ASSIST-01 ships only the waitlist
 * card: testers see it too, whatever the mode (the live card is ASSIST-04).
 */
export type AssistCardVariant = 'none' | 'waitlist';

export function assistCardVariant(config: AssistPublicConfig): AssistCardVariant {
  if (config.mode === 'off' && !config.isTester) return 'none';
  return 'waitlist';
}

/** The facts the card needs about the listing it sits on (R17). */
export interface AssistListingFacts {
  /** `listing` for a native listing, `syndicated` for an indexed one. */
  source: 'listing' | 'syndicated';
  /** The raw `property_type` column, not a display label. */
  propertyType: string | null | undefined;
  city: string | null | undefined;
  status: string | null | undefined;
  agentId: string | null | undefined;
}

/**
 * R17 eligibility for the card: a native, published listing whose type and
 * city are in the pilot, and not the viewer's own listing (R9). Syndicated
 * pages get no card until decision D-SYND. `register_assist_interest` applies
 * the same rules on the server.
 */
export function isAssistEligibleListing(
  listing: AssistListingFacts,
  config: AssistPublicConfig,
  viewerId: string | null | undefined,
): boolean {
  if (listing.source !== 'listing') return false;
  if (listing.status !== 'published') return false;
  if (!listing.propertyType || !config.pilotPropertyTypes.includes(listing.propertyType)) return false;
  if (!listing.city || !config.pilotCities.includes(listing.city)) return false;
  if (viewerId && listing.agentId && viewerId === listing.agentId) return false;
  return true;
}

/** What `AssistRoute` does with a mode it has (or has not yet) resolved. */
export type AssistRouteDecision = 'wait' | 'allow' | 'redirect';

export function decideAssistRoute(
  state: { resolved: boolean; mode: AssistMode; isTester: boolean },
  minMode: 'waitlist' | 'live',
): AssistRouteDecision {
  // R18.4: loading is not `off`. A tester on a cold load must never be sent
  // away before their tester status arrives.
  if (!state.resolved) return 'wait';
  if (state.isTester) return 'allow';
  if (state.mode === 'live') return 'allow';
  if (state.mode === 'waitlist' && minMode === 'waitlist') return 'allow';
  return 'redirect';
}

/** The ways an Assist RPC can refuse, as the UI needs to tell them apart. */
export type AssistFailure = 'not_live' | 'ineligible' | 'auth' | 'failed';

/** Map a PostgREST error message to an AssistFailure. */
export function classifyAssistError(message: string | null | undefined): AssistFailure {
  if (!message) return 'failed';
  if (message === 'assist_not_live') return 'not_live';
  if (message === 'assist_listing_ineligible') return 'ineligible';
  if (message === 'assist_auth_required' || /JWT|jwt/.test(message)) return 'auth';
  return 'failed';
}

// Plain interfaces rather than unions on `ok`: tsconfig has `strict` off, and
// without strictNullChecks a boolean discriminant does not narrow.

/** `ok` with `created`, or not `ok` with `failure`. */
export interface AssistRegisterOutcome {
  ok: boolean;
  created?: boolean;
  failure?: AssistFailure;
}

/** `ok` with `joined`, or not `ok` with `failure`. */
export interface AssistMineOutcome {
  ok: boolean;
  joined?: boolean;
  failure?: AssistFailure;
}

export function parseRegisterResult(raw: unknown): AssistRegisterOutcome {
  if (raw && typeof raw === 'object' && (raw as Record<string, unknown>).joined === true) {
    return { ok: true, created: (raw as Record<string, unknown>).created === true };
  }
  return { ok: false, failure: 'failed' };
}

export function parseMineResult(raw: unknown): AssistMineOutcome {
  if (raw && typeof raw === 'object' && typeof (raw as Record<string, unknown>).joined === 'boolean') {
    return { ok: true, joined: (raw as Record<string, unknown>).joined === true };
  }
  return { ok: false, failure: 'failed' };
}
